import * as B from '@blueprintjs/core';
import geojson from 'geojson';
import * as I from 'immutable';
import React, {FormEvent} from 'react';

import AppNav from 'app/components/AppNav';
import {NewPortfolioLayersSelector} from 'app/components/Library/PortfolioLayersSelector';
import ErrorPropertiesCallout from 'app/components/ManagePortfolio/ErrorPropertiesCallout';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiFeatureCollection,
  HydratedFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {
  ApiOrganization,
  ApiOrganizationUser,
  getMeasurementSystem,
} from 'app/modules/Remote/Organization';
import {MapInteractionProvider} from 'app/pages/MonitorProjectView/Map';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import * as CONSTANTS from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {isFeatureOversized} from 'app/utils/polygonSplit';

import ManagePropertiesFileUploader from './ManageFeaturesFileUploader';
import ManageFeaturesMap from './ManageFeaturesMap';
import {ManageFeaturesActions, ManageFeaturesState} from './ManageFeaturesProvider';
import ManageFeaturesTable from './ManageFeaturesTable';
import {DrawPropertiesState} from './ManagePropertiesDraw';
import ManagePropertiesParcelOverlaySelector from './ManagePropertiesParcelOverlaySelector';
import {PORTFOLIO_MAX_ACRES, PropertyGroup, buildPropertyGroups} from './rowStatus';
import cs from './styles.module.scss';

import {ManagePropertiesMode, isDrawnPropertiesMode} from '.';

// Ok for a lot of these fields to be blank because this ApiFeatureCollection is only used for display purposes
// in the ManageProperties components
export const BLANK_API_FEATURE_COLLECTION: ApiFeatureCollection = {
  id: 1,
  organizationId: '00000000-0000-0000-0000-000000000000',
  type: 'FeatureCollection',
  name: 'Example Property',
  product: 'lens',
  createdAt: '2019-03-20T19:13:31.643263+00:00',
  updatedAt: '2019-03-20T19:13:31.643263+00:00',
  processingEndDate: null,
  processingStartDate: null,
  isArchived: false,
  bounds: {
    coordinates: [
      [
        [-122.13426941555338, 34.303227191563536],
        [-122.13426941555338, 40.423925695110974],
        [-118.87179824683717, 40.423925695110974],
        [-118.87179824683717, 34.303227191563536],
        [-122.13426941555338, 34.303227191563536],
      ],
    ],
    type: 'Polygon',
  },
  staticBounds: null,
  tiles: null,
  schema: {
    fields: {},
    geoType: 'POLYGON',
  },
  views: [],
  status: null,
  processingConfig: {
    enrolledLayers: {},
    processingMetadata: {},
  },
  supportedLayersForProduct: {},
  processingLayersBySource: [],
};

const BLANK_FEATURE_COLLECTION: Omit<HydratedFeatureCollection, 'features'> = I.fromJS(
  BLANK_API_FEATURE_COLLECTION
);

export const BLANK_HYDRATED_FEATURE_COLLECTION: HydratedFeatureCollection =
  BLANK_FEATURE_COLLECTION.set('features', I.List());

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  loggedInUserActions: LoggedInUserActions;
  manageFeaturesState: ManageFeaturesState;
  manageFeaturesActions: ManageFeaturesActions;
  loading: boolean;
  mode: ManagePropertiesMode;
  firebaseToken: string | null;
  agentPrompt?: React.ReactNode;
  //suggestedLayers is only used in agent mode, and renders above the footer
  suggestedLayers?: React.ReactNode;
  //drawPropertiesState is used in draw, parcel, and agent modes and undefined in other modes
  drawPropertiesState?: DrawPropertiesState;
  //overlayNames is always used in overlays mode and always undefined in other modes
  overlayNames?: string[];
  loadingOverlays?: boolean;
  //existingOverlayName is used in updateOverlay mode to display the name of the overlay being updated
  existingOverlayName?: string;
  //existingOverlayFeatureCollection is used in updateOverlay mode to render the current overlay on the map
  existingOverlayFeatureCollection?: HydratedFeatureCollection | null;
}

const ManageFeaturesView: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  loggedInUserActions,
  firebaseToken,
  agentPrompt,
  suggestedLayers,
  manageFeaturesState: {
    filename,
    uploadedFeatureCollection,
    isConvertingFile,
    problems,
    nameField,
    nameValues,
    partNameField,
    selectedFeature,
    groupModeByName,
    isUploading,
    selectedFeatureIds,
    areaUnit,
    featureCollectionErrors,
    featureCollectionWarnings,
    allUploadableFeatureIds,
    existingFeatureCollection,
    portfolioName,
    warningsMap,
    partNameValues,
    selectedLayerKeys,
    selectedOverlayIds,
    hoverProperties,
    originalGeometries,
    removedFeatureIds,
  },
  manageFeaturesActions: {
    setFilename,
    setUploadedFeatureCollection,
    setFilePath,
    setNameField,
    setNameValues,
    setPartNameField,
    setSelectedFeature,
    convertFile,
    uploadFeatures,
    uploadDrawnFeatures,
    splitFeature,
    removeFeature,
    setGroupModeForName,
    setSelectedFeatureIds,
    setProblems,
    setFeatureCollectionErrors,
    setPortfolioName,
    setPartNameValues,
    setSelectedLayerKeys,
    setSelectedOverlayIds,
    setHoverProperties,
  },
  mode,
  drawPropertiesState,
  loading,
  loadingOverlays,
  overlayNames,
  existingOverlayName,
  existingOverlayFeatureCollection,
}) => {
  const warningsCount = React.useMemo(
    () =>
      Object.keys(warningsMap).reduce(
        (warningsCount, id) => warningsCount + (warningsMap[id]?.length || 0),
        0
      ),
    [warningsMap]
  );

  // We keep 2 featureCollections in this component.
  // uploadedFeatureCollection is a geojson.FeatureCollection that is returned from the API
  // upon conversion of the user's file. This only changes if the user uploads a different
  // file. This is typed as a NumberedFeatureCollection to reduce casting when doing lots of
  // look up and comparing operations across these featureCollections.
  // mapFeatureCollection is derived from that and is the HydratedFeatureCollection that we use
  // to display on the map. This gets updated with the name fields the user selected, if they select
  // or unselect properties etc.
  const mapFeatureCollection: HydratedFeatureCollection | null = React.useMemo(() => {
    if (!uploadedFeatureCollection) {
      return null;
    }
    const features: I.List<I.ImmutableOf<ApiFeature>> = I.List([
      ...uploadedFeatureCollection.features.flatMap((feature) => {
        const featureId = feature.id;
        // If a feature in the file is not selected, don't include it in the featureCollection
        if (!selectedFeatureIds.has(featureId) || !feature.properties) {
          return [];
        }

        let mapFeature: I.ImmutableOf<ApiFeature> = I.fromJS({
          ...feature,
          id: featureId,
        });

        if (nameValues) {
          // Overlays don't have a canonical `name` property; the "name" column in the
          // editor maps to whichever hover attribute was picked first (`nameField`).
          // Writing into `properties.name` here would clobber the original `name`
          // attribute when it's also a selected hover attribute, so for overlays we
          // write the edited value into `properties[nameField]` instead — and skip
          // the override entirely when no hover attribute is selected yet.
          if (mode === 'overlay' || mode === 'updateOverlay') {
            if (nameField) {
              const properties = mapFeature.get('properties') as I.Map<string, unknown>;
              mapFeature = mapFeature.set(
                'properties',
                properties.set(nameField, nameValues.get(featureId)) as any
              );
            }
          } else {
            mapFeature = mapFeature.setIn(['properties', 'name'], nameValues.get(featureId));
          }
        }

        const partName = partNameValues?.get(featureId);
        if (partName) {
          mapFeature = mapFeature.setIn(['properties', 'multiFeaturePartName'], partName);
        }
        return [mapFeature];
      }),
    ]);
    let blankWithFeatures = BLANK_FEATURE_COLLECTION.set('features', features);

    if (mode === 'overlay' || mode === 'updateOverlay') {
      blankWithFeatures = blankWithFeatures.set('name', portfolioName || existingOverlayName || '');
      if (hoverProperties.length > 0) {
        blankWithFeatures = blankWithFeatures.set(
          'views',
          I.fromJS([
            {
              type: CONSTANTS.VIEW_TYPE_OVERLAY_METADATA,
              hover_properties: hoverProperties,
            },
          ])
        );
      }
    }
    return blankWithFeatures;
  }, [
    mode,
    nameValues,
    portfolioName,
    existingOverlayName,
    partNameValues,
    selectedFeatureIds,
    uploadedFeatureCollection,
    hoverProperties,
  ]);

  // Geometries to render on the map as a thick white outline (no fill): the
  // pre-split originals retained by the provider AND any currently-oversized
  // features that aren't in the selected set (e.g. a just-drawn too-large
  // polygon, or an oversized feature in a freshly-uploaded file). Without
  // this, oversized features would never appear on the map until the user
  // splits them — which means the map wouldn't even zoom to where they are.
  const outlineGeometries: geojson.Geometry[] = React.useMemo(() => {
    const oversized: geojson.Geometry[] = [];
    if (uploadedFeatureCollection) {
      for (const f of uploadedFeatureCollection.features) {
        if (selectedFeatureIds.has(f.id) || removedFeatureIds.has(f.id)) continue;
        if (isFeatureOversized(f)) oversized.push(f.geometry);
      }
    }
    return [...originalGeometries, ...oversized];
  }, [originalGeometries, uploadedFeatureCollection, selectedFeatureIds, removedFeatureIds]);

  // Property count reflects the submit-time grouping decision: a combined
  // group becomes one property regardless of how many locations are checked,
  // while a "keep separate" group contributes one property per selected
  // location. Counting unique names alone would understate separate mode
  // whenever the user hasn't yet disambiguated location names.
  const propertyCount = React.useMemo(() => {
    if (!uploadedFeatureCollection) return 0;
    const identities = new Set<string>();
    for (const f of uploadedFeatureCollection.features) {
      if (!selectedFeatureIds.has(f.id)) continue;
      const name = nameValues?.get(f.id) ?? '';
      const groupMode = groupModeByName.get(name) ?? 'group';
      identities.add(groupMode === 'group' ? `g:${name}` : `i:${f.id}`);
    }
    return identities.size;
  }, [uploadedFeatureCollection, selectedFeatureIds, nameValues, groupModeByName]);

  const [locationCount, mapFeatureCollectionArea] = React.useMemo(() => {
    if (!mapFeatureCollection) {
      return [0, 0];
    }

    const locationCount = mapFeatureCollection.get('features').size;

    const hasArea = mapFeatureCollection.get('features').every((f) => {
      const geometryType = f?.getIn(['geometry', 'type']);
      return geometryType === 'Polygon' || geometryType === 'MultiPolygon';
    });

    const totalArea = hasArea
      ? featureCollectionUtils.featureCollectionArea(mapFeatureCollection, areaUnit)
      : 0;

    return [locationCount, totalArea];
  }, [mapFeatureCollection, areaUnit]);

  const existingFeatureCollectionArea = React.useMemo(() => {
    return existingFeatureCollection
      ? featureCollectionUtils.featureCollectionArea(existingFeatureCollection, areaUnit)
      : 0;
  }, [existingFeatureCollection, areaUnit]);

  // Area of everything still in the list (selected or not), minus removed rows.
  // The portfolio limit is about the whole pending addition, so excluded
  // oversize rows count toward it until the user dismisses them with ×.
  const pendingAdditionArea = React.useMemo(() => {
    if (!uploadedFeatureCollection) return 0;
    const features = uploadedFeatureCollection.features.filter((f) => !removedFeatureIds.has(f.id));
    const hasArea = features.every(
      (f) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
    );
    if (!features.length || !hasArea) return 0;
    return featureCollectionUtils.featureCollectionArea(
      I.fromJS({type: 'FeatureCollection', features}) as HydratedFeatureCollection,
      areaUnit
    );
  }, [uploadedFeatureCollection, removedFeatureIds, areaUnit]);

  const portfolioCap = conversionUtils.convert(
    PORTFOLIO_MAX_ACRES,
    CONSTANTS.UNIT_AREA_ACRE,
    areaUnit
  );

  // Check if property additions will cause portfolio to exceed the cap. Overlays
  // aren't portfolios, so the cap doesn't apply to them.
  const isOverlayMode = mode === 'overlay' || mode === 'updateOverlay';
  const exceededPortfolioSize =
    !isOverlayMode && existingFeatureCollectionArea + pendingAdditionArea > portfolioCap;

  // Aggregate (collection-level) errors shown in the top banner — isolated here
  // so future entirety-level limits plug into one place, distinct from the
  // per-row RowView statuses. (Convert-time aborts — file too large, too many
  // features, etc. — are surfaced separately via `problems` before rows load.)
  const collectionErrors = React.useMemo(() => {
    const errors: string[] = [];
    if (exceededPortfolioSize) {
      const total = existingFeatureCollectionArea + pendingAdditionArea;
      const over = total - portfolioCap;
      const unitLabel = areaUnit === 'areaHectare' ? 'hectare' : 'acre';
      errors.push(
        `Adding these properties would exceed the ${conversionUtils.numberWithCommas(
          Math.round(portfolioCap)
        )} ${unitLabel} portfolio limit. This selection totals ${conversionUtils.numberWithCommas(
          Math.round(total)
        )} ${unitLabel} — about ${conversionUtils.numberWithCommas(
          Math.round(over)
        )} ${unitLabel} over. Remove some properties or upload them to another portfolio.`
      );
    }
    return errors;
  }, [
    exceededPortfolioSize,
    existingFeatureCollectionArea,
    pendingAdditionArea,
    portfolioCap,
    areaUnit,
  ]);

  const [isSelectLayersModalOpen, setIsSelectLayersModalOpen] = React.useState(false);

  // Here we make sure that new users go through available datasets
  const [datasetsHaveBeenReviewed, setDatasetsHaveBeenReviewed] =
    React.useState(!!existingFeatureCollection);

  //these next few consts take care of much of the logic to control what is hidden/shown based on mode
  const modeValidator = (mode: ManagePropertiesMode) => {
    if (!['draw', 'parcel', 'upload', 'overlay', 'updateOverlay', 'agentUpload'].includes(mode))
      return false;
    if (mode === 'updateOverlay') return true;
    if (mode === 'overlay') return !duplicateOverlayNameWarning;
    // Duplicate names are now handled per-group in the table (combine into one
    // multi-location property, or keep separate); no global part-name gate.
    else return nameField && !exceededPortfolioSize;
  };

  const readyToSubmit = (): boolean => {
    return [
      !!uploadedFeatureCollection,
      selectedFeatureIds.size > 0,
      existingFeatureCollection || !!portfolioName || mode === 'updateOverlay',
      mode === 'overlay' ||
        mode === 'updateOverlay' ||
        !!(existingFeatureCollection || datasetsHaveBeenReviewed),
      modeValidator(mode),
    ].every((condition) => condition);
  };

  const showFileUploader = mode === 'upload' || mode === 'overlay' || mode === 'updateOverlay';

  const duplicateOverlayNameWarning =
    mode === 'overlay' && portfolioName && overlayNames?.includes(portfolioName);

  // This sort relies on the nameField selected, not the actual nameValues to avoid properties jumping
  // around and resorting while editing names
  const sortedFeatures = React.useMemo(() => {
    if (!nameField || !uploadedFeatureCollection) {
      return [];
    }

    // Only sort features from a file. For draw mode, this would cause features
    // to resort themselves as you type since we are directly modifying the
    // uploadedFeatureCollection
    return mode === 'upload' || mode === 'overlay' || mode === 'updateOverlay'
      ? uploadedFeatureCollection.features.sort((a, b) => {
          const aField: string | number = a!.properties![nameField] ?? '';
          const bField: string | number = b!.properties![nameField] ?? '';
          return featureUtils.alphanumericSort(aField, bField);
        })
      : uploadedFeatureCollection.features;
  }, [uploadedFeatureCollection, nameField, mode]);

  const featuresWithErrors = React.useMemo(() => {
    if (!uploadedFeatureCollection) return [];
    return uploadedFeatureCollection.features.filter((f) => {
      if (!f.properties) return false;
      if (f.properties['__UPSTREAM_PROBLEMS']) return true;
      if (mode !== 'overlay' && mode !== 'updateOverlay' && nameValues && !nameValues.get(f.id))
        return true;
      return false;
    });
  }, [uploadedFeatureCollection, nameValues, mode]);

  // Group features into properties by name (mirroring the portfolio property
  // table), then split into "Needs attention" / "Ready" sections. Removed rows
  // (dismissed via ×) drop out entirely.
  // Three sections, in priority order: Warnings → Errors → Ready. A group with
  // both takes the warning slot so the warning stays visible — the errors
  // already gate the checkbox, so demoting them in the section sort doesn't
  // change what can be submitted.
  // `nameValues` only changes on commit (NameInput's onBlur/Enter), so the section partition is
  // stable mid-edit.
  const [errorsGroups, warningsGroups, readyGroups]: [
    PropertyGroup[],
    PropertyGroup[],
    PropertyGroup[],
  ] = React.useMemo(() => {
    const groups = buildPropertyGroups({
      features: sortedFeatures,
      removedFeatureIds,
      nameOf: (feature) => nameValues?.get(feature.id) ?? '',
      warningsOf: (feature) => warningsMap[feature.id] ?? null,
      partNameOf: (feature) => partNameValues?.get(feature.id) ?? '',
      mode,
    });
    return [
      groups.filter((g) => g.hasErrors && !g.hasWarning),
      groups.filter((g) => g.hasWarning),
      groups.filter((g) => !g.hasErrors && !g.hasWarning),
    ];
  }, [sortedFeatures, removedFeatureIds, nameValues, warningsMap, partNameValues, mode]);

  // Candidate file attributes for the per-group "location name" picker.
  const partNameFieldOptions = React.useMemo(() => {
    const props = uploadedFeatureCollection?.features[0]?.properties;
    return props ? Object.keys(props).filter((key) => !/^_+UPSTREAM_/.test(key)) : [];
  }, [uploadedFeatureCollection]);

  const newFeatures = mapFeatureCollection?.get('features') || I.List([]);
  const existingFeatures = existingFeatureCollection?.get('features') || I.List([]);

  // We can be confident these ids wont overlap because the new features
  // returned from the convertFile endpoint always start at 1 through
  // the number of features while the exising features are high up into the 9
  // digit range now so it's very unlikely there will be overlap.
  const allFeatures: I.ImmutableOf<ApiFeature[]> = I.List(newFeatures.concat(existingFeatures));

  const measurementSystem = getMeasurementSystem(organization);

  const getDisabledTooltipText = (): string | undefined => {
    if (mode === 'updateOverlay') {
      return selectedFeatureIds.size === 0 ? 'Select at least one feature' : undefined;
    }

    const nameMissingMessage = `Please name your ${mode === 'overlay' ? 'overlay' : 'portfolio'}`;
    const datasetsNeedReviewMessage = 'Please review datasets';
    const datasetsNeedReviewAndNameMessage = `${nameMissingMessage} and review datasets`;
    const datasetsNeedReview =
      mode !== 'overlay' && !existingFeatureCollection && !datasetsHaveBeenReviewed;

    if (!portfolioName && datasetsNeedReview) {
      return datasetsNeedReviewAndNameMessage;
    }

    if (!portfolioName) {
      return nameMissingMessage;
    }

    if (datasetsNeedReview) {
      return datasetsNeedReviewMessage;
    }
  };

  return (
    <>
      <AppNav
        organization={organization}
        profile={profile}
        selectedProject={null}
        loggedInUserActions={loggedInUserActions}
      />
      {/* We are only using the hover interactions provided by the MapInteractionProvider.
        The select interactions could be used in the future to scroll to an entry in the table when
        a feature is clicked on. Note that there is another prop used in this file called selectedFeatureIds that contains
        all the featureIds to upload */}
      <MapInteractionProvider
        features={allFeatures}
        selectedFeatureIds={mapFeatureCollection ? selectedFeatureIds : I.Set([])}
        setSelectedFeatureIds={() => null}
      >
        {({hoveredFeatureIds, hoverFeatureById}) => {
          return (
            <div className={cs.content}>
              {loading && <B.Spinner />}
              {!loading && (
                <div className={cs.form}>
                  <h2 className={cs.header}>
                    {existingFeatureCollection ? (
                      `Add Properties to ${portfolioName}`
                    ) : mode === 'updateOverlay' ? (
                      `Updating overlay: ${existingOverlayName}`
                    ) : (
                      <B.InputGroup
                        type="text"
                        large
                        className={cs.nameInput}
                        value={portfolioName || undefined}
                        placeholder={`Name your ${mode === 'overlay' ? 'overlay' : 'portfolio'}…`}
                        onChange={(event: FormEvent<HTMLInputElement>) => {
                          const element = event.currentTarget as HTMLInputElement;
                          const {value} = element;
                          setPortfolioName(value);
                        }}
                      />
                    )}
                    {mode === 'draw' && drawPropertiesState && (
                      <B.ButtonGroup className={cs.drawingModeButtons}>
                        <B.Tooltip
                          content={'Click here to begin drawing a property'}
                          disabled={drawPropertiesState.editMode}
                          intent={'primary'}
                        >
                          <B.Button
                            minimal={drawPropertiesState.editMode}
                            active={drawPropertiesState.editMode}
                            text={'Draw'}
                            intent={'primary'}
                            onClick={() => {
                              drawPropertiesState.setFeatureToEdit(null);
                              drawPropertiesState.setEditMode((editMode) => !editMode);
                            }}
                          />
                        </B.Tooltip>
                        {drawPropertiesState.editMode && !!drawPropertiesState.setDrawingMode && (
                          <B.ButtonGroup>
                            <B.Button
                              icon="polygon-filter"
                              onClick={() => drawPropertiesState.setDrawingMode!('draw_polygon')}
                              active={
                                drawPropertiesState.editMode &&
                                drawPropertiesState.drawingMode === 'draw_polygon'
                              }
                            ></B.Button>
                            <B.Button
                              icon="widget"
                              onClick={() => drawPropertiesState.setDrawingMode!('draw_rectangle')}
                              active={
                                drawPropertiesState.editMode &&
                                drawPropertiesState.drawingMode === 'draw_rectangle'
                              }
                            ></B.Button>
                          </B.ButtonGroup>
                        )}
                      </B.ButtonGroup>
                    )}
                  </h2>
                  <div className={cs.formContent}>
                    {mode === 'agentUpload' && agentPrompt}
                    {mode === 'parcel' && drawPropertiesState && (
                      <ManagePropertiesParcelOverlaySelector
                        drawPropertiesState={drawPropertiesState}
                        organization={organization}
                        loadingOverlays={!!loadingOverlays}
                      />
                    )}
                    {showFileUploader && (
                      <ManagePropertiesFileUploader
                        isConvertingFile={isConvertingFile}
                        problems={problems}
                        filename={filename}
                        onFilenameChange={(file) => {
                          // Setting the file name before validating the file type so that
                          // the user can see the file name with the error, if applicable.
                          setFilename(file.name);
                          setUploadedFeatureCollection(null);
                          setProblems(null);
                          setFeatureCollectionErrors(null);
                          setFilePath(null);
                          setNameField(null);
                          setPartNameField(null);
                          setHoverProperties([]);
                          convertFile(file, mode);
                        }}
                        uploadedFeatureCollection={uploadedFeatureCollection}
                        nameField={nameField}
                        setNameField={setNameField}
                        setPartNameField={setPartNameField}
                        mode={mode}
                        hoverProperties={hoverProperties}
                        setHoverProperties={setHoverProperties}
                      />
                    )}
                    {nameValues && (
                      <div className={cs.reviewProperties}>
                        {collectionErrors.length > 0 && (
                          <B.Callout className={cs.callout} intent={B.Intent.DANGER}>
                            {collectionErrors.map((collectionError, i) => (
                              <div key={i}>{collectionError}</div>
                            ))}
                          </B.Callout>
                        )}
                        {duplicateOverlayNameWarning && (
                          <B.Callout className={cs.callout} intent={B.Intent.DANGER}>
                            You already have an overlay named {portfolioName}. Choose another name
                            to save your overlay.
                          </B.Callout>
                        )}
                        {!!sortedFeatures.length && (
                          <ManageFeaturesTable
                            selectedFeatureIds={selectedFeatureIds}
                            allUploadableFeatureIds={allUploadableFeatureIds}
                            setSelectedFeatureIds={setSelectedFeatureIds}
                            areaUnit={areaUnit}
                            nameValues={nameValues}
                            nameField={nameField}
                            setNameValues={setNameValues}
                            partNameValues={partNameValues}
                            setPartNameValues={setPartNameValues}
                            groupModeByName={groupModeByName}
                            setGroupModeForName={setGroupModeForName}
                            partNameField={partNameField}
                            setPartNameField={setPartNameField}
                            partNameFieldOptions={partNameFieldOptions}
                            errorsGroups={errorsGroups}
                            warningsGroups={warningsGroups}
                            readyGroups={readyGroups}
                            hoverFeatureById={hoverFeatureById}
                            hoveredFeatureIds={hoveredFeatureIds}
                            setSelectedFeature={setSelectedFeature}
                            setUploadedFeatureCollection={setUploadedFeatureCollection}
                            mode={mode}
                            drawPropertiesState={drawPropertiesState}
                            removeFeature={removeFeature}
                            splitFeature={splitFeature}
                            isConvertingFile={isConvertingFile}
                            // the first hoverProperty is already the name column
                            // so we pass the rest of hoverProperties as selected columns here.
                            additionalColumnProperties={hoverProperties.slice(1, undefined)}
                          />
                        )}
                        {/* Modes without the file uploader (draw, parcel, agent) convert their
                        features at submit time, so this is the only place their conversion
                        problems can surface. */}
                        {!showFileUploader && problems && (
                          <B.Callout className={cs.callout} intent={B.Intent.DANGER} icon={null}>
                            <ul>
                              {problems.map((p, x) => (
                                <li key={x}>{p}</li>
                              ))}
                            </ul>
                          </B.Callout>
                        )}
                        {featureCollectionErrors && (
                          <B.Callout className={cs.callout} intent={B.Intent.DANGER} icon={null}>
                            <ul>
                              {featureCollectionErrors.map((p, x) => (
                                <li key={x}>{p}</li>
                              ))}
                            </ul>
                          </B.Callout>
                        )}
                      </div>
                    )}
                  </div>
                  {/**Outside formContent, so it stays in view above the footer while the
                  property table scrolls. Rendered before the footer's nameValues gate because
                  suggestions can land before the generated properties do. */}
                  {mode === 'agentUpload' && suggestedLayers}
                  {/**in overlays mode, you can upload even if no nameValues,
                  as long as you have a file */}
                  {(nameValues ||
                    ((mode === 'overlay' || mode === 'updateOverlay') &&
                      uploadedFeatureCollection)) && (
                    <div className={cs.uploadFooter}>
                      {featureCollectionWarnings && featureCollectionWarnings.size > 0 && (
                        <B.Callout intent={B.Intent.WARNING} icon={'warning-sign'}>
                          {Array.from(featureCollectionWarnings).map((warning) => (
                            <React.Fragment key={warning}>
                              {' '}
                              <span>{getFCWarning(warning)}</span>
                            </React.Fragment>
                          ))}
                        </B.Callout>
                      )}
                      <ErrorPropertiesCallout
                        featuresWithErrors={featuresWithErrors}
                        downloadFilename="property-upload-errors"
                        warningsCount={warningsCount}
                      />
                      <B.Callout
                        intent={readyToSubmit() ? B.Intent.SUCCESS : B.Intent.NONE}
                        icon={null}
                      >
                        <div className={cs.confirmCalloutContent}>
                          <span className={cs.confirmCalloutText}>
                            {(mode === 'upload' || isDrawnPropertiesMode(mode)) && (
                              <>
                                {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}{' '}
                                across {locationCount}{' '}
                                {locationCount === 1 ? 'location' : 'locations'} with a total of{' '}
                                {conversionUtils.numberWithCommas(
                                  mapFeatureCollectionArea.toString()
                                )}{' '}
                                {areaUnit === 'areaHectare' ? 'hectares' : 'acres'} will be added to{' '}
                                {portfolioName ? (
                                  <span className={cs.bold}>{portfolioName + '.'}</span>
                                ) : (
                                  'your Lens Portfolio.'
                                )}
                              </>
                            )}
                            {(mode === 'overlay' || mode === 'updateOverlay') && (
                              <>
                                {mode === 'updateOverlay' ? 'Update' : 'Upload'}{' '}
                                {portfolioName || existingOverlayName ? (
                                  <span className={cs.bold}>
                                    {(portfolioName || existingOverlayName) + '.'}
                                  </span>
                                ) : (
                                  'your overlay'
                                )}
                              </>
                            )}
                          </span>
                          <div className={cs.footerButtons}>
                            {mapFeatureCollection &&
                              !existingFeatureCollection &&
                              mode !== 'overlay' &&
                              mode !== 'updateOverlay' && (
                                <B.Tooltip
                                  content={'Choose which datasets to process in your portfolio'}
                                  disabled={datasetsHaveBeenReviewed}
                                >
                                  <B.AnchorButton
                                    intent={
                                      datasetsHaveBeenReviewed ? B.Intent.NONE : B.Intent.SUCCESS
                                    }
                                    text="Review datasets"
                                    onClick={() => {
                                      setDatasetsHaveBeenReviewed(true);
                                      setIsSelectLayersModalOpen(true);
                                    }}
                                  />
                                </B.Tooltip>
                              )}
                            <B.Tooltip
                              disabled={readyToSubmit()}
                              content={getDisabledTooltipText()}
                              intent={'danger'}
                            >
                              <B.AnchorButton
                                text="Submit"
                                intent={readyToSubmit() ? B.Intent.SUCCESS : B.Intent.NONE}
                                disabled={!readyToSubmit()}
                                onClick={() =>
                                  isDrawnPropertiesMode(mode)
                                    ? uploadDrawnFeatures()
                                    : uploadFeatures()
                                }
                                loading={isUploading}
                              />
                            </B.Tooltip>
                          </div>
                        </div>
                      </B.Callout>
                    </div>
                  )}
                </div>
              )}
              {!loading && (
                <ManageFeaturesMap
                  newFeatureCollection={mapFeatureCollection}
                  hoverFeatureById={hoverFeatureById}
                  hoveredFeatureIds={hoveredFeatureIds}
                  selectedFeature={selectedFeature}
                  measurementSystem={measurementSystem}
                  firebaseToken={firebaseToken}
                  outlineGeometries={outlineGeometries}
                  {...(mode === 'updateOverlay'
                    ? {
                        mode,
                        existingOverlayFeatureCollection: existingOverlayFeatureCollection ?? null,
                      }
                    : {mode, existingFeatureCollection})}
                  {...(isDrawnPropertiesMode(mode) && drawPropertiesState
                    ? {
                        allowDrawing: true,
                        isEditMode: drawPropertiesState.editMode,
                        setIsEditMode: drawPropertiesState.setEditMode,
                        featureToEdit: drawPropertiesState.featureToEdit,
                        setFeatureToEdit: drawPropertiesState.setFeatureToEdit,
                        onMapFeatureClick: drawPropertiesState.onMapFeatureClick,
                        drawingMode: drawPropertiesState.drawingMode,
                        selectedOverlayFc: drawPropertiesState.selectedOverlayFc,
                      }
                    : {allowDrawing: false})}
                />
              )}
              {!loading && !existingFeatureCollection && mapFeatureCollection && (
                <NewPortfolioLayersSelector
                  isOpen={isSelectLayersModalOpen}
                  onClose={() => setIsSelectLayersModalOpen(false)}
                  selectedLayerKeys={selectedLayerKeys}
                  setSelectedLayerKeys={setSelectedLayerKeys}
                  selectedOverlayIds={selectedOverlayIds}
                  setSelectedOverlayIds={setSelectedOverlayIds}
                />
              )}
            </div>
          );
        }}
      </MapInteractionProvider>
    </>
  );
};

function getFCWarning(apiWarningString: string) {
  if (
    apiWarningString.includes(
      'region where imagery requests may go through an extra step of approval'
    )
  ) {
    return 'One or more properties are in a region where imagery requests may go through an extra step of approval — our team will be in touch if needed.';
  }
  return '';
}

export default ManageFeaturesView;
